import React, { useEffect, useRef, useState } from "react";
import { Crown, Target, TrendingUp } from "lucide-react";

const TRACKER_BG =
    "https://images.unsplash.com/photo-1709525617656-5498eefc7d03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBnb2xmJTIwY291cnNlJTIwc3Vuc2V0fGVufDB8fHx8MTc4MTI2Mzc1MHww&ixlib=rb-4.1.0&q=85";

const useCountUp = (target, duration = 1400) => {
    const [value, setValue] = useState(0);
    const ref = useRef(null);
    const started = useRef(false);

    useEffect(() => {
        if (typeof IntersectionObserver === "undefined") {
            setValue(target);
            return;
        }
        const node = ref.current;
        if (!node) return;

        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting && !started.current) {
                        started.current = true;
                        const start = performance.now();
                        const tick = (now) => {
                            const t = Math.min(1, (now - start) / duration);
                            const eased = 1 - Math.pow(1 - t, 3);
                            setValue(Math.floor(target * eased));
                            if (t < 1) requestAnimationFrame(tick);
                            else setValue(target);
                        };
                        requestAnimationFrame(tick);
                    }
                });
            },
            { threshold: 0.3 }
        );
        obs.observe(node);
        return () => obs.disconnect();
    }, [target, duration]);

    return [value, ref];
};

const formatUSD = (n) =>
    "$" +
    n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PrizeCard = ({ label, amount, icon: Icon, badge, testId }) => {
    const [val, ref] = useCountUp(amount);
    return (
        <div
            ref={ref}
            data-testid={testId}
            className="relative group overflow-hidden border border-[#D4AF37]/30 bg-[#0B1325]/70 backdrop-blur-xl p-8 md:p-10 transition-all hover:border-[#D4AF37]/70 hover:-translate-y-1"
        >
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent opacity-60" />

            <div className="flex items-start justify-between mb-8">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center bg-[#D4AF37]/5">
                        <Icon size={18} className="text-[#D4AF37]" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">
                            Grand Purse
                        </div>
                        <div className="font-display font-bold text-white text-sm tracking-wider uppercase">
                            {label}
                        </div>
                    </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF5A00] border border-[#FF5A00]/40 bg-[#FF5A00]/10 px-2 py-1">
                    {badge}
                </div>
            </div>

            <div className="font-mono-num font-bold text-[#D4AF37] text-5xl md:text-6xl lg:text-7xl leading-none gold-glow">
                {formatUSD(val)}
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#94A3B8] mt-4">
                Winner-Take-All Grand Prize · USD
            </div>

            {/* Corner accents */}
            <span className="absolute top-2 left-2 w-3 h-3 border-t border-l border-[#D4AF37]" />
            <span className="absolute top-2 right-2 w-3 h-3 border-t border-r border-[#D4AF37]" />
            <span className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-[#D4AF37]" />
            <span className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-[#D4AF37]" />
        </div>
    );
};

export const PrizeTracker = ({ stats }) => {
    const [payout, payoutRef] = useCountUp(stats?.total_payout || 0);
    const [aces, acesRef] = useCountUp(stats?.total_aces || 0);

    return (
        <section
            id="prizes"
            className="relative overflow-hidden border-b border-white/10 py-20 md:py-28"
            data-testid="prize-tracker-section"
        >
            <div className="absolute inset-0">
                <img
                    src={TRACKER_BG}
                    alt=""
                    className="w-full h-full object-cover opacity-15"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B1325] via-[#0B1325]/95 to-[#0B1325]" />
            </div>

            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div>
                        <div className="text-[11px] tracking-[0.4em] uppercase font-bold text-[#D4AF37] mb-3">
                            — Section 01 / Prize Architecture
                        </div>
                        <h2 className="font-display font-black uppercase text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-none">
                            The Purse. The Bounty.
                            <br />
                            <span className="text-[#94A3B8]">The Reckoning.</span>
                        </h2>
                    </div>
                    <p className="text-[#94A3B8] text-sm max-w-md leading-relaxed">
                        Two divisions, two grand prizes, and a live bounty on every verified
                        hole-in-one shot across the four majors.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <PrizeCard
                        label="Top Male Golfer"
                        amount={stats?.grand_prize_male || 50000}
                        icon={Crown}
                        badge="Men"
                        testId="prize-card-male"
                    />
                    <PrizeCard
                        label="Top Female Golfer"
                        amount={stats?.grand_prize_female || 50000}
                        icon={Crown}
                        badge="Women"
                        testId="prize-card-female"
                    />
                </div>

                {/* Hole-in-One bounty */}
                <div
                    className="relative mt-6 border border-[#FF5A00]/40 bg-[#0B1325]/80 backdrop-blur-xl overflow-hidden"
                    data-testid="hole-in-one-tracker"
                >
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FF5A00] to-transparent" />
                    <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-white/10">
                        <div className="p-8 lg:p-10 lg:col-span-1 flex items-center gap-4">
                            <div className="w-12 h-12 border border-[#FF5A00]/50 bg-[#FF5A00]/10 flex items-center justify-center shrink-0">
                                <Target size={20} className="text-[#FF5A00]" />
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] mb-1">
                                    Live Bounty
                                </div>
                                <div className="font-display font-black text-white text-2xl leading-tight">
                                    $1,000.00 USD
                                </div>
                                <div className="text-xs text-[#94A3B8] mt-1">
                                    Awarded for every verified hole-in-one
                                </div>
                            </div>
                        </div>

                        <div ref={payoutRef} className="p-8 lg:p-10 flex items-center gap-4">
                            <div className="w-12 h-12 border border-[#D4AF37]/40 bg-[#D4AF37]/5 flex items-center justify-center shrink-0">
                                <TrendingUp size={20} className="text-[#D4AF37]" />
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] mb-1">
                                    Total Payout To Date
                                </div>
                                <div
                                    className="font-mono-num font-bold text-[#D4AF37] text-3xl leading-tight"
                                    data-testid="hio-total-payout"
                                >
                                    {formatUSD(payout)}
                                </div>
                            </div>
                        </div>

                        <div ref={acesRef} className="p-8 lg:p-10 flex items-center gap-4">
                            <div className="w-12 h-12 border border-white/20 flex items-center justify-center shrink-0">
                                <span className="font-display font-black text-white text-lg">
                                    ⛳
                                </span>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] mb-1">
                                    Total Aces Verified
                                </div>
                                <div
                                    className="font-mono-num font-bold text-white text-3xl leading-tight"
                                    data-testid="hio-total-aces"
                                >
                                    {String(aces).padStart(3, "0")}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default PrizeTracker;
